import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import {
  getAllCountries,
  getStatesByCountry,
  hasStates,
  validatePostalCode,
  getStateLabelByCountry,
  getPostalCodeLabelByCountry,
  getCountryName,
} from '../lib/addressUtils';

/**
 * Reusable address form component with international support
 * @param {Object} props
 * @param {Object} props.address - Current address object
 * @param {Function} props.onChange - Handler receiving updated address object
 * @param {boolean} props.disabled - Disable all fields (for hosted listings)
 */
export default function AddressForm({ address = {}, onChange, disabled = false }) {
  const [countries] = useState(() => getAllCountries());
  const [states, setStates] = useState([]);
  const [postalCodeError, setPostalCodeError] = useState(null);

  // Determine current country code (either ISO code or full name)
  const currentCountryCode = address?.country;
  const currentCountryISOCode = currentCountryCode === 'United States' ? 'US' :
    currentCountryCode === 'Canada' ? 'CA' :
    currentCountryCode === 'United Kingdom' ? 'GB' :
    currentCountryCode || 'US';

  // Update states list when country changes
  useEffect(() => {
    if (currentCountryISOCode && hasStates(currentCountryISOCode)) {
      setStates(getStatesByCountry(currentCountryISOCode));
    } else {
      setStates([]);
    }
  }, [currentCountryISOCode]);

  // Backfill the displayed country into the address object. The dropdown falls
  // back to showing the default country when address.country is empty, but that
  // default was never written to the address — so a listing whose address came
  // from the pipeline (address1/city/state/zip set, country blank) shows
  // "United States" yet saves WITHOUT a country, tripping the API's
  // "Country is required when providing an address" validation. Whenever there's
  // address data but no country set, persist the country we're already showing.
  useEffect(() => {
    const hasData = address?.address1 || address?.city || address?.state || address?.zip;
    if (hasData && !address?.country) {
      onChange({ ...address, country: getCountryName(currentCountryISOCode) });
    }
  }, [address, currentCountryISOCode, onChange]);

  // Find selected country for react-select
  const selectedCountry = countries.find(c => c.value === currentCountryISOCode) || null;

  // Handle country change
  const handleCountryChange = (selectedOption) => {
    const newCountryCode = selectedOption?.value || '';
    const newCountryName = getCountryName(newCountryCode);

    onChange({
      ...address,
      country: newCountryName,
      // Clear state if new country doesn't have states
      state: hasStates(newCountryCode) ? address.state : '',
    });
  };

  // Handle address field changes
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    onChange({
      ...address,
      [name]: value,
    });
  };

  // Handle postal code validation on blur
  const handlePostalCodeBlur = (e) => {
    const postalCode = e.target.value;
    const validation = validatePostalCode(currentCountryISOCode, postalCode);

    if (!validation.valid) {
      setPostalCodeError(validation.message);
    } else {
      setPostalCodeError(null);
    }
  };

  // Custom styles for react-select to match Tailwind forms
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '38px',
      borderColor: state.isFocused ? '#6b7280' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 1px #6b7280' : 'none',
      '&:hover': {
        borderColor: '#6b7280',
      },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 50,
    }),
  };

  const showStateField = hasStates(currentCountryISOCode);
  const stateLabel = getStateLabelByCountry(currentCountryISOCode);
  const postalCodeLabel = getPostalCodeLabelByCountry(currentCountryISOCode);

  return (
    <>
      {/* Country */}
      <div className="col-span-6 sm:col-span-3">
        <label htmlFor="country" className="block text-sm font-medium text-gray-700">
          Country
        </label>
        <Select
          id="country"
          name="country"
          value={selectedCountry}
          onChange={handleCountryChange}
          options={countries}
          isDisabled={disabled}
          styles={selectStyles}
          className="mt-1"
          placeholder="Select country..."
          formatOptionLabel={(option) => (
            <div className="flex items-center">
              <span className="mr-2">{option.flag}</span>
              <span>{option.label}</span>
            </div>
          )}
        />
      </div>

      {/* Street Address Line 1 */}
      <div className="col-span-6">
        <label htmlFor="address1" className="block text-sm font-medium text-gray-700">
          Street address
        </label>
        <input
          type="text"
          name="address1"
          id="address1"
          value={address?.address1 || ''}
          onChange={handleFieldChange}
          disabled={disabled}
          autoComplete="street-address"
          className="mt-1 focus:ring-gray-500 focus:border-gray-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
      </div>

      {/* Street Address Line 2 */}
      <div className="col-span-6">
        <label htmlFor="address2" className="block text-sm font-medium text-gray-700">
          Apartment, suite, etc. (optional)
        </label>
        <input
          type="text"
          name="address2"
          id="address2"
          value={address?.address2 || ''}
          onChange={handleFieldChange}
          disabled={disabled}
          autoComplete="street-address"
          className="mt-1 focus:ring-gray-500 focus:border-gray-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
      </div>

      {/* City */}
      <div className="col-span-6 sm:col-span-6 lg:col-span-2">
        <label htmlFor="city" className="block text-sm font-medium text-gray-700">
          City
        </label>
        <input
          type="text"
          name="city"
          id="city"
          value={address?.city || ''}
          onChange={handleFieldChange}
          disabled={disabled}
          className="mt-1 focus:ring-gray-500 focus:border-gray-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
      </div>

      {/* State/Province (conditional) */}
      {showStateField && (
        <div className="col-span-6 sm:col-span-3 lg:col-span-2">
          <label htmlFor="state" className="block text-sm font-medium text-gray-700">
            {stateLabel}
          </label>
          <select
            name="state"
            id="state"
            value={address?.state || ''}
            onChange={handleFieldChange}
            disabled={disabled}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-gray-500 focus:border-gray-500 sm:text-sm rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">Select {stateLabel}</option>
            {states.map(state => (
              <option key={state.value} value={state.value}>
                {state.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Postal Code */}
      <div className={`col-span-6 sm:col-span-3 ${showStateField ? 'lg:col-span-2' : 'lg:col-span-4'}`}>
        <label htmlFor="zip" className="block text-sm font-medium text-gray-700">
          {postalCodeLabel}
        </label>
        <input
          type="text"
          name="zip"
          id="zip"
          value={address?.zip || ''}
          onChange={handleFieldChange}
          onBlur={handlePostalCodeBlur}
          disabled={disabled}
          autoComplete="postal-code"
          className={`mt-1 focus:ring-gray-500 focus:border-gray-500 block w-full shadow-sm sm:text-sm rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed ${
            postalCodeError ? 'border-red-300' : 'border-gray-300'
          }`}
        />
        {postalCodeError && (
          <p className="mt-1 text-sm text-red-600">{postalCodeError}</p>
        )}
      </div>
    </>
  );
}
